import React from "react";
import {
  FiX,
  FiHash,
  FiArrowRight,
  FiUser,
  FiClock,
  FiLayers,
  FiDatabase,
  FiCheckCircle,
} from "react-icons/fi";
import { clsx } from "clsx";
import dayjs from "dayjs";

const BlockTransactionsModal = ({ isOpen, onClose, block, team }) => {
  if (!isOpen || !block) return null;

  const isRedTeam = team === "red";
  const accentColor = isRedTeam ? "text-red-400" : "text-blue-400";
  const borderColor = isRedTeam ? "border-red-400/30" : "border-blue-400/30";
  const bgColor = isRedTeam ? "bg-red-400/5" : "bg-blue-400/5";
  const gradientColor = isRedTeam
    ? "from-red-500/10 to-transparent"
    : "from-blue-500/10 to-transparent";

  // const totalOutput = (block.transactions || []).reduce(
  //   (sum, tx) => sum + (Number(tx.amount) || 0),
  //   0
  // );
  const totalFees = (block.transactions || [])
    .filter((tx) => tx.status === "Mining Cost")
    .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-300">
      <div
        className={clsx(
          "relative w-full max-w-3xl max-h-[90vh] overflow-hidden glass-card border flex flex-col shadow-[0_0_50px_rgba(0,0,0,0.5)] animate-in zoom-in-95 duration-300",
          borderColor
        )}
      >
        {/* Header */}
        <div
          className={clsx(
            "p-6 border-b border-white/10 flex items-center justify-between bg-gradient-to-r",
            gradientColor
          )}
        >
          <div className="flex items-center gap-4">
            <div className={clsx("p-3 rounded-2xl bg-white/5 border", borderColor)}>
              <FiLayers className={clsx("text-2xl", accentColor)} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl text-white tracking-tight uppercase">
                  Block{" "}
                  <span className={accentColor}>
                    #{block.index !== undefined ? block.index : "???"}
                  </span>
                </h2>
                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] uppercase tracking-wider">
                  <FiCheckCircle size={10} /> Confirmed
                </div>
              </div>
              <p className="text-xs text-text-muted mt-1 font-mono flex items-center gap-2">
                <FiHash size={12} /> {block.hash}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-all text-text-muted hover:text-white"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar bg-black/20">
          {/* Summary Section */}
          <section>
            <h3 className="text-xs text-text-muted uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
              <div
                className={clsx("w-1 h-3 rounded-full", isRedTeam ? "bg-red-400" : "bg-blue-400")}
              />
              Summary
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryItem
                icon={<FiClock />}
                label="Timestamp"
                value={
                  block.timestamp
                    ? dayjs
                      .unix(
                        typeof block.timestamp === "number"
                          ? block.timestamp
                          : dayjs(block.timestamp).unix()
                      )
                      .format("MMM DD, YYYY HH:mm:ss")
                    : "N/A"
                }
              />
              <SummaryItem
                icon={<FiClock />}
                label="Mining cost"
                value={`- ${totalFees.toLocaleString()} Astra`}
              />
              <SummaryItem
                icon={<FiLayers />}
                label="Transactions"
                value={block.transactions?.length || 0}
              />
              <SummaryItem icon={<FiCheckCircle />} label="Difficulty" value="2" />
            </div>
          </section>

          {/* Transactions List */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs text-text-muted uppercase tracking-[0.2em] flex items-center gap-2">
                <div
                  className={clsx("w-1 h-3 rounded-full", isRedTeam ? "bg-red-400" : "bg-blue-400")}
                />
                Transactions
              </h3>
              <span className="text-[10px] text-text-muted px-2 py-0.5 rounded bg-white/5 border border-white/5">
                {block.transactions?.length || 0} ITEMS
              </span>
            </div>

            <div className="space-y-4">
              {block.transactions && block.transactions.length > 0 ? (
                block.transactions.map((tx, idx) => (
                  <div
                    key={tx.id || idx}
                    className={clsx(
                      "overflow-hidden rounded-2xl border transition-all hover:bg-white/5 group",
                      borderColor,
                      bgColor
                    )}
                  >
                    <div className="p-4 border-b border-white/5 flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] text-text-muted bg-white/5 px-2 py-0.5 rounded-full border border-white/10">
                          TX #{idx + 1}
                        </span>
                        <code
                          className="text-[10px] text-primary font-mono opacity-60 group-hover:opacity-100 transition-opacity"
                          title={tx.hash}
                        >
                          {tx.hash}
                        </code>
                      </div>
                      <div className="flex items-center gap-4">
                        {tx.status?.includes("Coinbase") && (
                          <span className="text-[10px] text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded border border-emerald-400/20 uppercase">
                            Coinbase
                          </span>
                        )}
                        <div className="text-sm text-white">
                          {tx.amount}{" "}
                          <span className="text-[10px] text-text-muted opacity-60">ASTRA</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-4 relative">
                      <AddressSection
                        label="From"
                        address={tx.sender === "SYSTEM" ? "Block Reward (SYSTEM)" : tx.sender}
                        isSystem={tx.sender === "SYSTEM"}
                      />

                      {/* Flow Arrow */}
                      <div className="flex justify-center md:pt-4">
                        <div
                          className={clsx(
                            "w-10 h-10 rounded-xl border flex items-center justify-center bg-black/40 backdrop-blur-sm shadow-lg transition-transform group-hover:scale-110",
                            borderColor
                          )}
                        >
                          <FiArrowRight className={clsx("text-xl", accentColor)} />
                        </div>
                      </div>

                      <AddressSection
                        label="To"
                        address={tx.receiver === "SYSTEM" ? "Mining Fee (SYSTEM)" : tx.receiver}
                        isSystem={tx.receiver === "SYSTEM"}
                      />
                    </div>

                    {tx.signature && (
                      <div className="px-4 py-2 bg-black/40 border-t border-white/5 flex items-center justify-between">
                        <span className="text-[9px] text-text-muted uppercase tracking-widest">
                          Signature Verified
                        </span>
                        <code className="text-[9px] text-text-muted font-mono truncate max-w-[200px] opacity-40 group-hover:opacity-100 transition-opacity">
                          {tx.signature}
                        </code>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-12 bg-white/5 rounded-2xl border border-dashed border-white/10">
                  <FiDatabase size={32} className="mx-auto mb-3 opacity-20" />
                  <p className="text-text-muted text-sm uppercase tracking-widest">
                    No transactions available
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

const SummaryItem = ({ icon, label, value }) => (
  <div className="bg-white/5 p-4 rounded-2xl border border-white/5 hover:border-white/10 transition-all group">
    <div className="flex items-center gap-2 text-text-muted mb-2 text-[9px] uppercase tracking-widest group-hover:text-white transition-colors">
      {React.cloneElement(icon, { size: 12 })}
      {label}
    </div>
    <p className="text-sm text-white truncate">{value}</p>
  </div>
);

const AddressSection = ({ label, address, isSystem }) => (
  <div className="space-y-2">
    <span className="text-[9px] text-text-muted uppercase tracking-widest flex items-center gap-1">
      <FiUser size={10} /> {label}
    </span>
    <div
      className={clsx(
        "p-3 rounded-xl border font-mono text-xs truncate transition-all",
        isSystem
          ? "bg-emerald-500/5 border-emerald-500/20 text-emerald-400"
          : "bg-black/20 border-white/5 text-white/80"
      )}
      title={address}
    >
      {address}
    </div>
  </div>
);

export default BlockTransactionsModal;
